import React, { useState, useEffect, useRef } from "react";
import {
  AppLayout,
  ContentLayout,
  Tabs,
  Box,
  Header,
  SpaceBetween,
  Spinner,
  Flashbar,
} from "@amzn/awsui-components-react";
import { useHistory, useLocation } from "react-router-dom";

import SideNav from "../../Home/SideNav";
import { getCurriculum, listCalendar, listCertifications, listFaq } from "./api";
import RaiseLanding from "./RaiseLanding";
import CoreCurriculum from "./CoreCurriculum";
import TrainingCalendar from "./TrainingCalendar";
import GetCertified from "./GetCertified";
import Faq from "./Faq";
import "./styles.scss";

const TAB_IDS = ["home", "curriculum", "calendar", "certified", "faq"];

// Static intro copy (not admin-editable — see RaiseLanding for the rationale).
const CURRICULUM_INTRO =
  "The RAISE curriculum includes 8 skill sets which progress from AI Aware to AI Champion. AI Aware and AI Enabled apply to everyone in WW Accounting, and AI Champion is an extension of the ATEAM, role based for selected champions from the LT.";
const CERTIFICATION_INTRO =
  "Certifications will be launched in Q4 2026 for AI Aware and AI Enabled, allowing you to validate your skillset.";

// Which read fetches each tab's data. Home is static (no fetch).
const TAB_FETCHERS = {
  curriculum: getCurriculum,
  calendar: listCalendar,
  certified: listCertifications,
  faq: listFaq,
};

/**
 * RAISE section shell. Data is loaded lazily PER TAB: the active tab fetches on
 * mount, and other tabs fetch the first time they're opened (then cached in
 * state so re-visiting a tab doesn't refetch). Each tab shows its own spinner
 * while its data loads. The active tab is synced to the URL (?tab=).
 */
export default function RaiseHome({ embedded = false } = {}) {
  const history = useHistory();
  const location = useLocation();

  // In embedded mode (admin preview modal) the tab lives in local state only —
  // it must not read or write the page URL.
  const tabFromQuery = embedded ? null : new URLSearchParams(location.search).get("tab");
  const initialTab = TAB_IDS.includes(tabFromQuery) ? tabFromQuery : "home";

  const [activeTabId, setActiveTabId] = useState(initialTab);
  const [navOpen, setNavOpen] = useState(true);
  // Per-tab data, loading flag, and error — keyed by tab id.
  const [dataByTab, setDataByTab] = useState({});
  const [loadingTab, setLoadingTab] = useState({});
  const [errorByTab, setErrorByTab] = useState({});
  // Tracks which tabs we've already kicked off a fetch for. A ref (not state)
  // so it doesn't retrigger the effect — otherwise setState in the effect would
  // re-run it, cancel the in-flight fetch, and hang on "Loading...".
  const requestedTabs = useRef({});

  // On load, if the URL has no valid ?tab=, reflect the current tab (home) in
  // the query so the URL is explicit. Skipped in embedded (modal) mode.
  useEffect(() => {
    if (embedded) return;
    const current = new URLSearchParams(location.search).get("tab");
    if (!TAB_IDS.includes(current)) {
      const params = new URLSearchParams(location.search);
      params.set("tab", activeTabId);
      history.replace({ search: params.toString() });
    }
    // Only on mount — subsequent tab changes are handled by onTabChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch a tab's data once, the first time it becomes active (Home is static).
  useEffect(() => {
    const fetcher = TAB_FETCHERS[activeTabId];
    if (!fetcher) return; // Home (or unknown) — nothing to fetch.
    if (requestedTabs.current[activeTabId]) return; // already fetched/fetching.
    requestedTabs.current[activeTabId] = true;

    const tabId = activeTabId;
    setLoadingTab((m) => ({ ...m, [tabId]: true }));
    // Clear any prior error for this tab so a retry that succeeds actually
    // shows its data — tabContent checks errorByTab first, so a stale error
    // would otherwise keep the tab pinned to the error Flashbar forever.
    setErrorByTab((m) => ({ ...m, [tabId]: undefined }));
    (async () => {
      try {
        const result = await fetcher();
        // Store the resolved value as-is (coalescing only null/undefined to an
        // empty object) so a legitimately empty read is "loaded-but-empty", not
        // mistaken for "still loading". Coercing to null here would make the
        // child components fall back to their own perpetual loading spinner.
        setDataByTab((m) => ({ ...m, [tabId]: result ?? {} }));
      } catch (e) {
        setErrorByTab((m) => ({
          ...m,
          [tabId]: e?.errors?.[0]?.message || "Failed to load this tab.",
        }));
        // Allow a retry on a later visit if it failed.
        requestedTabs.current[tabId] = false;
      } finally {
        setLoadingTab((m) => ({ ...m, [tabId]: false }));
      }
    })();
  }, [activeTabId]);

  const onTabChange = (tabId) => {
    setActiveTabId(tabId);
    if (embedded) return; // don't touch the URL when previewing in a modal
    const params = new URLSearchParams(location.search);
    params.set("tab", tabId);
    history.replace({ search: params.toString() });
  };

  // Wraps a tab's content with its own loading spinner / error message.
  const tabContent = (tabId, render) => {
    if (errorByTab[tabId]) {
      return (
        <Flashbar items={[{ type: "error", content: errorByTab[tabId], dismissible: false }]} />
      );
    }
    // Spin only while the fetch is in flight or hasn't started. Once it
    // resolves, dataByTab[tabId] is always a defined object (empty reads are
    // stored as {}), so an empty backend read renders the tab's empty state
    // instead of spinning forever.
    if (loadingTab[tabId] || dataByTab[tabId] === undefined) {
      return (
        <Box padding="l">
          <Spinner /> Loading...
        </Box>
      );
    }
    return render(dataByTab[tabId]);
  };

  const tabs = [
    { id: "home", label: "Home", content: <RaiseLanding onNavigate={onTabChange} /> },
    {
      id: "curriculum",
      label: "Core Curriculum",
      content: tabContent("curriculum", (d) => (
        <CoreCurriculum curriculum={d} intro={CURRICULUM_INTRO} />
      )),
    },
    {
      id: "calendar",
      label: "Training Calendar",
      content: tabContent("calendar", (d) => <TrainingCalendar calendar={d} />),
    },
    {
      id: "certified",
      label: "Get Certified",
      content: tabContent("certified", (d) => (
        <GetCertified certifications={d?.certifications || []} intro={CERTIFICATION_INTRO} />
      )),
    },
    {
      id: "faq",
      label: "FAQ",
      content: tabContent("faq", (d) => <Faq faq={d} />),
    },
  ];

  const inner = (
    <Tabs
      activeTabId={activeTabId}
      onChange={({ detail }) => onTabChange(detail.activeTabId)}
      tabs={tabs}
    />
  );

  // Embedded (admin preview): NO ContentLayout — nesting a second ContentLayout
  // inside the admin's high-contrast ContentLayout corrupts its width after the
  // modal closes. Just render the header + tabs plainly.
  if (embedded) {
    return (
      <SpaceBetween size="l">
        <Header variant="h1" description="Responsible AI Skills & Expertise">
          RAISE
        </Header>
        {inner}
      </SpaceBetween>
    );
  }

  const content = (
    <ContentLayout
      header={
        <Header variant="h1" description="Responsible AI Skills & Expertise">
          RAISE
        </Header>
      }
    >
      {inner}
    </ContentLayout>
  );

  return (
    <AppLayout
      content={content}
      contentType="default"
      navigation={<SideNav activeHref="/raise" />}
      navigationOpen={navOpen}
      onNavigationChange={({ detail }) => setNavOpen(detail.open)}
      toolsHide={true}
    />
  );
}
